import { type AbilitySummary, type OrgChartNode, type OrgChartView } from '@apm/contracts';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { EmptyState, ErrorState, Skeleton } from '../../components/ui/states';
import { useAuth } from '../../features/auth/auth-context';
import { boardApi, boardKeys } from '../../features/board/api';
import { InviteToProjectDialog } from '../../features/org-chart/components/invite-to-project-dialog';
import { OrgChartTree } from '../../features/org-chart/components/org-chart-tree';
import { PersonEditDialog } from '../../features/org-chart/components/person-edit-dialog';
import { RestructureEntry } from '../../features/org-chart/restructure/components/restructure-header';
import { RestructurePanel } from '../../features/org-chart/restructure/components/restructure-panel';
import { ProposeSplitPanel } from '../../features/org-chart/restructure/components/split-config-section';
import {
  SplitSelectionProvider,
  useSplitSelectionState,
  useTreeSelection,
} from '../../features/org-chart/restructure/components/split-selection';
import { emptyDraft } from '../../features/org-chart/restructure/split-draft';
import { apiFetch } from '../../lib/http';
import { orgTimezone } from '../../lib/org-timezone';

export const OrgChartSearch = z.object({
  /**
   * Đề xuất gộp / tách đang mở ở ngăn phải — APM-ORGMS-f (§3.7). uuid là chuỗi mà
   * `JSON.parse` không đọc nổi, nên nó tới schema nguyên dạng chuỗi: khai `z.string()` là
   * đúng (cạm bẫy search param của TanStack Router).
   */
  restructure: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/org-chart')({
  component: OrgChartPage,
  validateSearch: OrgChartSearch,
});

/**
 * Sơ đồ phân cấp tổ chức — APM-ORG-b.
 *
 * ## Vì sao là route riêng chứ không phải một mục trong `/admin`
 *
 * `/admin` hiện với người có `user:manage`, tức chỉ `ORG_ADMIN` và
 * `SUPER_ADMIN`. Sơ đồ này thì `DIRECTOR` và `LEADER` cũng phải xem được — và
 * họ không có `user:manage`, đúng như thiết kế: nhìn cây báo cáo và sửa hồ sơ
 * người khác là hai việc khác nhau.
 *
 * Không gắn quyền ở route: phạm vi do API quyết theo vai trò người gọi
 * (`org-chart.service.ts`), và người không có quyền nhận 403 rồi thấy một dòng
 * giải thích. Thanh điều hướng đã lọc theo `org_chart:read` nên đường vào bình
 * thường không bày ra cho ai không dùng được.
 */
function OrgChartPage(): ReactNode {
  const { t } = useTranslation();
  const chart = useQuery({
    queryKey: ['org-chart'],
    queryFn: () => apiFetch<OrgChartView>('/admin/org-chart'),
  });

  /*
    Sửa được hay không hỏi **quyền hiệu lực**, không hỏi `orgRole`.

    `user:manage` là cửa thật ở API. Suy từ vai trò thì mỗi lần ma trận đổi là
    chỗ này lệch theo — và nó lệch im lặng, thành một nút bấm vào ra 403.
  */
  const abilities = useQuery({
    queryKey: ['abilities'],
    queryFn: () => apiFetch<AbilitySummary>('/me/abilities'),
    staleTime: 5 * 60_000,
  });
  const granted = abilities.data?.global ?? [];
  const canEdit = granted.includes('user:manage');
  // Gộp / tách tổ chức — APM-ORGMS-f, §3.7: ngăn đề xuất mở theo `?restructure=<id>`.
  const { restructure: openId } = Route.useSearch();
  const navigate = useNavigate({ from: '/org-chart' });
  const openRestructure = (id: string | undefined): void =>
    void navigate({ search: (prev) => ({ ...prev, restructure: id }) });
  /*
    Tách tổ chức — APM-ORGMS-h (§3.7). Bản nháp sống ở trang: cây (chế độ chọn) và ngăn phải
    cùng sửa nó. Chỉ bản nháp của thứ **đang hiện** ở ngăn mới đưa cây vào chế độ chọn — bản
    nháp đề xuất mới không điều khiển cây khi ngăn đang mở một đề xuất khác.
  */
  const split = useSplitSelectionState();
  const { selection, prompt: branchPrompt } = useTreeSelection(split);
  const selecting =
    split.draft !== null && (split.owner === 'new' ? !openId : split.owner === openId);
  const [editing, setEditing] = useState<OrgChartNode | null>(null);
  const [inviting, setInviting] = useState<OrgChartNode | null>(null);
  const { user: me } = useAuth();

  /*
    Dự án mình **mời được người vào** — PM, hoặc vai trò tuỳ biến có
    `project:update`. Đọc từ `perProject` của `/me/abilities`, cùng nguồn guard
    dùng để chặn, nên nút mời không bao giờ hiện ở chỗ server sẽ trả 403.
  */
  const invitableIds = new Set(
    (abilities.data?.perProject ?? [])
      .filter((entry) => entry.permissions.includes('project:update'))
      .map((entry) => entry.projectId),
  );
  const projects = useQuery({
    queryKey: boardKeys.projects(),
    queryFn: () => boardApi.projects(),
    enabled: invitableIds.size > 0,
  });
  const invitableProjects = (projects.data ?? [])
    .filter((project) => invitableIds.has(project.id))
    .map((project) => ({ id: project.id, name: project.name }));

  return (
    <SplitSelectionProvider store={split}>
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-start gap-3" data-tour="org-chart-page">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h1 className="text-xl font-medium">{t('orgChart.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('orgChart.subtitle')}</p>
          </div>
          {/* Nút đề xuất, hoặc dải trạng thái khi đã có đề xuất đang mở (§3.7). Quyền đọc
            từ `/me/abilities` như `canEdit`, không suy từ `orgRole`. */}
          <RestructureEntry
            permissions={granted}
            orgId={me?.orgId}
            openId={openId}
            onOpen={openRestructure}
            onProposeSplit={() => split.start('new', emptyDraft(orgTimezone()))}
          />
        </header>

        {/* Cây bên trái, ngăn đề xuất bên phải (§3.7) — màn hẹp thì ngăn xuống dưới. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            {chart.isPending ? <Skeleton className="h-64" /> : null}
            {chart.isError ? <ErrorState message={t('orgChart.failed')} /> : null}

            {chart.data ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  <span>{t('orgChart.total', { count: chart.data.total })}</span>

                  {/* Vòng lặp quản lý: cắt im lặng thì sơ đồ thiếu người mà tổng vẫn
                đúng, nên người xem không có cách nào biết. */}
                  {selecting ? (
                    <span className="text-primary">
                      ·{' '}
                      {t('orgRestructure.split.treeHint', {
                        count: split.draft?.people.length ?? 0,
                      })}
                    </span>
                  ) : null}

                  {chart.data.cycleCount > 0 ? (
                    <span className="text-rag-amber">
                      · {t('orgChart.cycleWarning', { count: chart.data.cycleCount })}
                    </span>
                  ) : null}
                </div>

                {chart.data.roots.length === 0 ? (
                  <EmptyState title={t('orgChart.empty')} description={t('orgChart.emptyHelp')} />
                ) : (
                  <OrgChartTree
                    nodes={chart.data.roots}
                    onSelect={canEdit ? setEditing : undefined}
                    onInvite={invitableProjects.length > 0 ? setInviting : undefined}
                    // Không mời chính mình, và không mời người đã khoá tài khoản —
                    // họ không đăng nhập được để nhận lời mời.
                    invitable={(node) => node.isActive && node.userId !== me?.id}
                    selection={selecting ? selection : undefined}
                  />
                )}
              </div>
            ) : null}
          </div>

          {openId ? (
            <RestructurePanel
              key={openId}
              restructureId={openId}
              onClose={() => openRestructure(undefined)}
            />
          ) : split.owner === 'new' && split.draft ? (
            <ProposeSplitPanel
              store={split}
              onCancel={() => split.stop('new')}
              onCreated={(id) => {
                split.stop('new');
                openRestructure(id);
              }}
            />
          ) : null}
        </div>
        {branchPrompt}

        {editing ? <PersonEditDialog person={editing} onClose={() => setEditing(null)} /> : null}
        {inviting ? (
          <InviteToProjectDialog
            person={inviting}
            projects={invitableProjects}
            onClose={() => setInviting(null)}
          />
        ) : null}
      </div>
    </SplitSelectionProvider>
  );
}
